class Logger {
    static _instance = null;
    
    constructor() {
        if (Logger._instance) {
            return Logger._instance;
        }
        
        this.logs = new Map();
        this.errorCount = 0;
        this.maxLogs = 1000;
        this.environment = process.env.NODE_ENV;
        this.analyticsEnabled = !!window.analytics;
        this._retentionDays = 7;
        this._lastCleanup = Date.now();
        
        // Intervalle de nettoyage des logs
        setInterval(() => this._cleanupOldLogs(), 24 * 60 * 60 * 1000);
        
        Logger._instance = this;
    }

    static getInstance() {
        if (!Logger._instance) {
            Logger._instance = new Logger();
        }
        return Logger._instance;
    }

    // Log principal avec support des différents niveaux
    log(type, message, data = {}) {
        const logEntry = {
            id: crypto.randomUUID(),
            timestamp: new Date().toISOString(),
            type,
            message,
            environment: this.environment,
            level: this._getLogLevel(type),
            ...data
        };
        
        // Gestion de la taille maximale des logs
        if (this.logs.size >= this.maxLogs) {
            const oldestKey = Array.from(this.logs.keys())[0];
            this.logs.delete(oldestKey);
        }
        
        this.logs.set(logEntry.id, logEntry);
        
        // Log console en développement avec styles
        if (this.environment === 'development') {
            const styles = this._getLogStyles(type);
            console.log(
                `%c[${type.toUpperCase()}]%c ${message}`,
                styles.label,
                styles.message,
                data
            );
        }

        // Analytics si configuré
        if (this.analyticsEnabled) {
            try {
                window.analytics.track(type, {
                    ...logEntry,
                    data: JSON.stringify(data)
                });
            } catch (error) {
                console.error('Analytics tracking failed:', error);
            }
        }

        return logEntry.id;
    }

    // Gestion spécifique des erreurs
    error(error, context = {}) {
        this.errorCount++;
        
        // Formater l'erreur de manière détaillée
        const errorDetails = {
            name: error.name,
            message: error.message,
            code: error.code,
            stack: this.environment === 'development' ? error.stack : undefined,
            context,
            errorCount: this.errorCount,
            timestamp: new Date().toISOString()
        };

        // Log d'erreur avec catégorisation
        return this.log('error', error.message, {
            ...errorDetails,
            category: this._categorizeError(error)
        });
    }

    // Logging spécifique pour les transactions Web3
    transaction(tx, status, details = {}) {
        return this.log('transaction', `Transaction ${status}`, {
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: tx.value?.toString(),
            gasUsed: tx.gasUsed?.toString(),
            status,
            block: tx.blockNumber,
            timestamp: Date.now(),
            ...details
        });
    }

    // Logs spécifiques aux composants
    web3(action, details = {}) {
        return this.log('web3', action, {
            timestamp: Date.now(),
            ...details
        });
    }

    vesting(action, address, details = {}) {
        return this.log('vesting', action, {
            address,
            timestamp: Date.now(),
            ...details
        });
    }

    staking(action, address, details = {}) {
        return this.log('staking', action, {
            address,
            timestamp: Date.now(),
            ...details
        });
    }

    // Monitoring des performances
    performance(metric, value, details = {}) {
        return this.log('performance', metric, {
            value,
            timestamp: Date.now(),
            ...details
        });
    }

    // Récupération des logs filtrés
    getLogs(options = {}) {
        const {
            type = null,
            level = null,
            limit = 100,
            startDate = null,
            endDate = null
        } = options;

        let filteredLogs = Array.from(this.logs.values());

        if (type) {
            filteredLogs = filteredLogs.filter(log => log.type === type);
        }

        if (level) {
            filteredLogs = filteredLogs.filter(log => log.level === level);
        }

        if (startDate) {
            filteredLogs = filteredLogs.filter(log => 
                new Date(log.timestamp) >= new Date(startDate)
            );
        }

        if (endDate) {
            filteredLogs = filteredLogs.filter(log => 
                new Date(log.timestamp) <= new Date(endDate)
            );
        }

        return filteredLogs
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, limit);
    }

    // Méthodes utilitaires
    getErrorCount() {
        return this.errorCount;
    }

    clearLogs() {
        this.logs.clear();
        this.errorCount = 0;
        this._lastCleanup = Date.now();
    }

    exportLogs(format = 'json') {
        const logs = Array.from(this.logs.values());
        
        if (format === 'csv') {
            return this._exportToCSV(logs);
        }
        
        return JSON.stringify(logs, null, 2);
    }

    // Méthodes privées
    _cleanupOldLogs() {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - this._retentionDays);

        for (const [id, log] of this.logs.entries()) {
            if (new Date(log.timestamp) < cutoffDate) {
                this.logs.delete(id);
            }
        }

        this._lastCleanup = Date.now();
    }

    _categorizeError(error) {
        if (error.code === 4001) return 'USER_REJECTED';
        if (error.code === -32603) return 'RPC_ERROR';
        if (error.message?.includes('insufficient funds')) return 'INSUFFICIENT_FUNDS';
        if (error.message?.includes('nonce')) return 'NONCE_ERROR';
        if (error.message?.includes('gas')) return 'GAS_ERROR';
        return 'UNKNOWN';
    }

    _getLogLevel(type) {
        const levels = {
            error: 'ERROR',
            warning: 'WARN',
            transaction: 'INFO',
            web3: 'INFO',
            performance: 'DEBUG'
        };
        return levels[type] || 'INFO';
    }

    _getLogStyles(type) {
        const styles = {
            error: {
                label: 'background: #ff5252; color: white; padding: 2px 5px; border-radius: 3px;',
                message: 'color: #ff5252;'
            },
            warning: {
                label: 'background: #ffc107; color: black; padding: 2px 5px; border-radius: 3px;',
                message: 'color: #ffc107;'
            },
            transaction: {
                label: 'background: #2196f3; color: white; padding: 2px 5px; border-radius: 3px;',
                message: 'color: #2196f3;'
            },
            web3: {
                label: 'background: #4caf50; color: white; padding: 2px 5px; border-radius: 3px;',
                message: 'color: #4caf50;'
            },
            performance: {
                label: 'background: #9c27b0; color: white; padding: 2px 5px; border-radius: 3px;',
                message: 'color: #9c27b0;'
            }
        };

        return styles[type] || {
            label: 'background: #757575; color: white; padding: 2px 5px; border-radius: 3px;',
            message: 'color: #757575;'
        };
    }

    _exportToCSV(logs) {
        const headers = ['timestamp', 'type', 'level', 'message', 'details'];
        const rows = logs.map(log => [
            log.timestamp,
            log.type,
            log.level,
            log.message,
            JSON.stringify(log)
        ]);

        return [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');
    }
}

export default Logger.getInstance();
